import React, { useEffect, useState, useCallback, useRef } from 'react';
import '../../styles/components/LoadingScreen.css';
import preloadService from '../../services/preloadService';

/**
 * Componente de pantalla de carga
 * Muestra una animación y barra de progreso mientras se cargan los recursos críticos
 * 
 * @param {Object} props - Propiedades del componente
 * @param {Function} props.onLoadingComplete - Función que se llama cuando la carga se completa
 */
const LoadingScreen = ({ onLoadingComplete }) => {
  const [progress, setProgress] = useState(0);
  const loadingRef = useRef({
    isLoading: true,
    hasStarted: false,
    currentProgress: 0,
    simulationStarted: false
  });
  
  // Actualizar la referencia cuando cambia el progreso
  useEffect(() => {
    loadingRef.current.currentProgress = progress;
  }, [progress]);
  
  // Función para simular progreso adicional (para UI)
  const simulateRemainingProgress = useCallback(() => {
    // Evitar iniciar la simulación más de una vez
    if (loadingRef.current.simulationStarted) {
      return () => {};
    }
    
    loadingRef.current.simulationStarted = true;
    let currentProgress = Math.max(70, loadingRef.current.currentProgress);
    
    const interval = setInterval(() => {
      currentProgress += 3;
      
      if (currentProgress >= 100) {
        clearInterval(interval);
        console.log("Carga completada");
        
        setTimeout(() => {
          if (loadingRef.current.isLoading) {
            loadingRef.current.isLoading = false;
            onLoadingComplete();
          }
        }, 500);
      } else {
        setProgress(currentProgress);
      }
    }, 100);
    
    return () => clearInterval(interval);
  }, [onLoadingComplete]);
  
  useEffect(() => {
    console.log("LoadingScreen montada");
    
    // Evitar iniciar la carga más de una vez
    if (loadingRef.current.hasStarted) {
      return;
    }
    
    loadingRef.current.hasStarted = true;
    
    // Contador de recursos cargados
    let loadedResources = 0;
    const totalResources = preloadService.criticalResources.length;
    
    // Función para actualizar el progreso de carga
    const handleProgress = (url, success) => {
      loadedResources++;
      if (success) {
        console.log(`Recurso cargado (${loadedResources}/${totalResources}): ${url}`);
      } else {
        console.log(`Error cargando: ${url}`);
      }
      
      // Actualizar progreso (70% máximo para los recursos, el resto es animación)
      const resourceProgress = Math.floor((loadedResources / totalResources) * 70);
      setProgress(prev => Math.max(prev, resourceProgress));
    };
    
    // Iniciar precarga de recursos
    preloadService.preloadAllResources(handleProgress, simulateRemainingProgress);
    
    // Garantizar que la carga termine incluso si hay problemas
    const safetyTimer = setTimeout(() => {
      if (loadingRef.current.isLoading && loadingRef.current.currentProgress < 100) {
        console.log("Safety timer activado - Forzando finalización de carga");
        setProgress(100);
        setTimeout(() => {
          loadingRef.current.isLoading = false;
          onLoadingComplete();
        }, 500);
      }
    }, 7000); // 7 segundos máximo de pantalla de carga
    
    // Asegúrate de que la pantalla de carga dure al menos un mínimo de tiempo
    const minLoadTimer = setTimeout(() => {
      setProgress(prev => Math.max(prev, 30));
    }, 500);
    
    return () => {
      clearTimeout(minLoadTimer);
      clearTimeout(safetyTimer);
    };
  }, [simulateRemainingProgress, onLoadingComplete]); // Eliminamos progress de las dependencias
  
  return (
    <div className="loading-screen">
      <div className="loading-content">
        {/* Animación ligera con temática de cómic */}
        <div className="comic-animation">
          <div className="comic-bubble"></div>
          <div className="comic-bubble"></div>
          <div className="comic-bubble"></div>
        </div>
        
        <div className="loading-bar-container">
          <div 
            className="loading-bar" 
            style={{ width: `${progress}%` }}
          ></div>
        </div>
        <div className="loading-text">Cargando... {progress}%</div>
      </div>
    </div>
  );
};

export default LoadingScreen; 